// ═══════════════════════════════════════════════════════════════
// PAYPARK — Supabase Client & Auth Service
// ═══════════════════════════════════════════════════════════════

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://igwtnauecjpnjfevrmvw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imlnd3RuYXVlY2pwbmpmZXZybXZ3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzMwMzgxNjYsImV4cCI6MjA4ODYxNDE2Nn0.ZnDzUxmJm3bDIL59McVabdoHhlCMkiUqXEtjX6HdDnY';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: { eventsPerSecond: 10 },
  },
});

// ═══════════════════════════════════════════════════════════════
// AUTH SERVICE
// ═══════════════════════════════════════════════════════════════

export const AuthService = {

  async register({ username, email, phone, password }) {
    const { data: existingEmail } = await supabase
      .from('users')
      .select('id')
      .eq('email', email.toLowerCase())
      .maybeSingle();

    if (existingEmail) {
      return { error: { field: 'email', message: 'Αυτό το email χρησιμοποιείται ήδη. Συνδέσου ή κάνε ανάκτηση.' } };
    }

    const phoneNorm = phone.replace(/\s/g, '');
    const { data: existingPhone } = await supabase
      .from('users')
      .select('id')
      .eq('phone', phoneNorm)
      .maybeSingle();

    if (existingPhone) {
      return { error: { field: 'phone', message: 'Αυτός ο αριθμός τηλεφώνου χρησιμοποιείται ήδη.' } };
    }

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: email.toLowerCase(),
      password,
    });

    if (authError) return { error: { field: 'general', message: authError.message } };

    const { error: profileError } = await supabase
      .from('users')
      .insert({
        auth_id: authData.user.id,
        username: username.trim(),
        email: email.toLowerCase(),
        phone: phoneNorm,
      });

    if (profileError) return { error: { field: 'general', message: profileError.message } };

    return { data: authData.user };
  },

  async login({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase(),
      password,
    });

    if (error) {
      return { error: { field: 'general', message: 'Λάθος email ή κωδικός.' } };
    }

    const { data: profile } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', data.user.id)
      .single();

    await supabase
      .from('users')
      .update({ last_login: new Date().toISOString() })
      .eq('auth_id', data.user.id);

    return { data: { ...data, profile } };
  },

  async logout() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: profile } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', user.id)
      .single();

    return profile;
  },

  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// ═══════════════════════════════════════════════════════════════
// USER SERVICE
// ═══════════════════════════════════════════════════════════════

export const UserService = {

  async getProfile(userId) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },

  async checkBanStatus(userId) {
    const { data } = await supabase
      .from('users')
      .select('ban_give_until, ban_take_until, penalty_score, is_active')
      .eq('id', userId)
      .single();

    const now = new Date();
    return {
      isActive: data?.is_active,
      isBannedFromGiving: data?.ban_give_until && new Date(data.ban_give_until) > now,
      isBannedFromTaking: data?.ban_take_until && new Date(data.ban_take_until) > now,
      banGiveUntil: data?.ban_give_until,
      banTakeUntil: data?.ban_take_until,
      penaltyScore: data?.penalty_score,
    };
  },

  async getVehicles(userId) {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async addVehicle(userId, vehicle) {
    const { data, error } = await supabase
      .from('vehicles')
      .insert({ user_id: userId, ...vehicle })
      .select()
      .single();
    return { data, error };
  },

  async deleteVehicle(vehicleId) {
    const { error } = await supabase
      .from('vehicles')
      .delete()
      .eq('id', vehicleId);
    return { error };
  },
};

// ═══════════════════════════════════════════════════════════════
// LISTINGS SERVICE
// ═══════════════════════════════════════════════════════════════

export const ListingsService = {

  async createListing({ region, address, lat, lng, timing, waitMinutes, price, giver_earn, company_earn, scheduledAt, max_vehicle_length }) {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return { error: { message: 'Δεν είσαι συνδεδεμένος' } };

    const { data: profile } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', authUser.id)
      .single();

    if (!profile) return { error: { message: 'Δεν βρέθηκε το προφίλ σου' } };

    // Για scheduled listing, το expires_at είναι η προγραμματισμένη ώρα + 30 λεπτά
    // Για τώρα, είναι 15 λεπτά από τώρα
    let expiresAt;
    if(timing === 'later' && scheduledAt) {
      expiresAt = new Date(new Date(scheduledAt).getTime() + 30 * 60 * 1000).toISOString();
    } else {
      expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    }

    const { data, error } = await supabase
      .from('listings')
      .insert({
        giver_id: profile.id,
        region,
        address,
        lat,
        lng,
        price,
        giver_earn,
        company_earn,
        timing,
        wait_minutes: waitMinutes,
        scheduled_at: scheduledAt || null,
        status: 'active',
        expires_at: expiresAt,
        max_vehicle_length: max_vehicle_length || null,
      })
      .select()
      .single();

    return { data, error };
  },

  async searchListings(region, seekerSize = null, seekerLength = null) {
    const now = new Date().toISOString();
    let query = supabase
      .from('listings')
      .select('*, giver:users(username)')
      .eq('region', region)
      .eq('status', 'active')
      .gt('expires_at', now)
      .order('created_at', { ascending: false });

    // Φίλτρο μήκους — εμφάνιση μόνο θέσεων που χωράει το όχημα του Seeker
    // max_vehicle_length >= μήκος Seeker (αυστηρό φίλτρο, χωρίς null bypass)
    if (seekerLength) {
      query = query.gte('max_vehicle_length', seekerLength);
    }

    const { data, error } = await query;
    return { data, error };
  },

  subscribeToListings(region, callback) {
    return supabase
      .channel(`listings:${region}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'listings',
        filter: `region=eq.${region}`,
      }, callback)
      .subscribe();
  },

  async cancelListing(listingId) {
    const { error } = await supabase
      .from('listings')
      .update({ status: 'cancelled' })
      .eq('id', listingId);
    return { error };
  },
};

// ═══════════════════════════════════════════════════════════════
// DEALS SERVICE
// ═══════════════════════════════════════════════════════════════

export const DealsService = {

  async createDeal({ listingId }) {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return { error: { message: 'Δεν είσαι συνδεδεμένος' } };

    const { data: seekerProfile } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', authUser.id)
      .single();

    if (!seekerProfile) return { error: { message: 'Δεν βρέθηκε το προφίλ σου' } };

    const { data: listing } = await supabase
      .from('listings')
      .select('*')
      .eq('id', listingId)
      .single();

    if (!listing || listing.status !== 'active') {
      return { error: { message: 'Η θέση δεν είναι διαθέσιμη πλέον.' } };
    }

    const { data: deal, error } = await supabase
      .from('deals')
      .insert({
        listing_id: listingId,
        giver_id: listing.giver_id,
        seeker_id: seekerProfile.id,
        region: listing.region,
        price: listing.price,
        giver_earn: listing.giver_earn,
        company_earn: listing.company_earn,
        status: 'matched',
      })
      .select()
      .single();

    if (error) return { error };

    await supabase
      .from('listings')
      .update({ status: 'matched' })
      .eq('id', listingId);

    return { data: deal };
  },

  async updateLocation({ dealId, role, lat, lng }) {
    const update = role === 'giver'
      ? { giver_lat: lat, giver_lng: lng }
      : { seeker_lat: lat, seeker_lng: lng };

    const { data, error } = await supabase
      .from('deals')
      .update(update)
      .eq('id', dealId)
      .select()
      .single();

    if (data?.giver_lat && data?.seeker_lat) {
      const distance = calculateDistance(
        data.giver_lat, data.giver_lng,
        data.seeker_lat, data.seeker_lng
      );

      if (distance <= 10) {
        await supabase
          .from('deals')
          .update({
            status: 'completed',
            gps_confirmed: true,
            completed_at: new Date().toISOString(),
          })
          .eq('id', dealId);
      }
    }

    return { data, error };
  },

  async cancelDeal({ dealId, cancelledBy }) {
    const status = cancelledBy === 'giver' ? 'cancelled_by_giver' : 'cancelled_by_seeker';
    const { error } = await supabase
      .from('deals')
      .update({ status })
      .eq('id', dealId);
    return { error };
  },

  async getDealHistory(userId) {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .or(`giver_id.eq.${userId},seeker_id.eq.${userId}`)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  subscribeToDeal(dealId, callback) {
    return supabase
      .channel(`deal:${dealId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'deals',
        filter: `id=eq.${dealId}`,
      }, callback)
      .subscribe();
  },
};

// ═══════════════════════════════════════════════════════════════
// PENALTIES SERVICE
// ═══════════════════════════════════════════════════════════════

export const PenaltyService = {

  async addPenalty({ userId, dealId, type, banType }) {
    const { data: existing } = await supabase
      .from('penalties')
      .select('id')
      .eq('user_id', userId)
      .gte('created_at', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString());

    const level = (existing?.length || 0) + 1;
    const banUntil = level >= 3
      ? new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString()
      : null;

    const { data, error } = await supabase
      .from('penalties')
      .insert({
        user_id: userId,
        deal_id: dealId,
        type,
        penalty_level: Math.min(level, 3),
        ban_type: level >= 3 ? banType : null,
        ban_until: banUntil,
      })
      .select()
      .single();

    return { data, error, level };
  },

  async getUserPenalties(userId) {
    const { data, error } = await supabase
      .from('penalties')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },
};

// ═══════════════════════════════════════════════════════════════
// WALLET SERVICE
// ═══════════════════════════════════════════════════════════════

export const WalletService = {

  async getBalance(userId) {
    const { data } = await supabase
      .from('users')
      .select('wallet_balance')
      .eq('id', userId)
      .single();
    return data?.wallet_balance || 0;
  },

  async getTransactions(userId) {
    const { data, error } = await supabase
      .from('wallet_transactions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    return { data, error };
  },

  async getVouchers(userId) {
    const { data, error } = await supabase
      .from('vouchers')
      .select('*')
      .eq('user_id', userId)
      .eq('is_used', false)
      .gt('expires_at', new Date().toISOString());
    return { data, error };
  },
};

// ═══════════════════════════════════════════════════════════════
// NOTIFICATIONS SERVICE
// ═══════════════════════════════════════════════════════════════

export const NotificationService = {

  async getNotifications(userId) {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(30);
    return { data, error };
  },

  async markAsRead(notificationId) {
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);
  },

  async getUnreadCount(userId) {
    const { count } = await supabase
      .from('notifications')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .eq('is_read', false);
    return count || 0;
  },

  subscribeToNotifications(userId, callback) {
    return supabase
      .channel(`notifications:${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, callback)
      .subscribe();
  },
};

// ═══════════════════════════════════════════════════════════════
// REGION REQUESTS SERVICE
// ═══════════════════════════════════════════════════════════════

export const RegionService = {

  async requestNewRegion({ userId, regionName, street }) {
    const { data: existing } = await supabase
      .from('region_requests')
      .select('id, request_count')
      .eq('region_name', regionName)
      .eq('status', 'pending')
      .single();

    if (existing) {
      await supabase
        .from('region_requests')
        .update({ request_count: existing.request_count + 1 })
        .eq('id', existing.id);
    } else {
      await supabase
        .from('region_requests')
        .insert({ user_id: userId, region_name: regionName, street });
    }

    return { success: true };
  },
};

// ═══════════════════════════════════════════════════════════════
// HELPER: Υπολογισμός απόστασης GPS (Haversine formula)
// ═══════════════════════════════════════════════════════════════

export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(Δφ/2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ/2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}
